import React, { useCallback, useEffect, useRef } from 'react'
import styles from './MobileHeader.module.scss'
import TopHeader from './MainTopHeader/TopHeader'
import MenuContent from './MenuContent/MenuContent'
import classNames from 'classnames'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import { useHeaderContext } from '../HeaderContext'

const HEADERS_WITH_BORDER = ['mainTopHeader']

const MobileHeader = () => {
  const { mobileHeaderConfig, setMobileHeaderConfig } = useHeaderContext()
  const stickyRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const frameRef = useRef<number | null>(null)

  const { isMobileMenuOpen, mobileMenuContent, mobileTopHeader } = mobileHeaderConfig

  // Search keeps the page scrollable and renders its own panel inside the sticky bar,
  // so it is not an overlay state
  const isMenuOverlayOpen =
    isMobileMenuOpen && mobileMenuContent !== 'null' && mobileMenuContent !== 'search'

  useBodyScrollLock(isMenuOverlayOpen)

  // Written straight to the node instead of through state: measuring must not re-render
  // the whole header subtree. The frame also lets layout settle after a rotation or
  // after the scroll lock is applied.
  const updateTopValue = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
    }

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null

      if (!stickyRef.current || !menuRef.current) {
        return
      }

      const bottomPosition = Math.max(
        0,
        Math.floor(stickyRef.current.getBoundingClientRect().bottom)
      )
      menuRef.current.style.setProperty('--dynamic-top-menu', `${bottomPosition}px`)
    })
  }, [])

  // Deliberately not listening to `scroll`: on iOS the URL bar collapsing fires scroll
  // events while the header reports transient rects, which made the overlay jitter.
  useEffect(() => {
    updateTopValue()

    window.addEventListener('resize', updateTopValue)
    window.addEventListener('orientationchange', updateTopValue)

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateTopValue) : null

    if (resizeObserver && stickyRef.current) {
      resizeObserver.observe(stickyRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateTopValue)
      window.removeEventListener('orientationchange', updateTopValue)
      resizeObserver?.disconnect()

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [updateTopValue])

  useEffect(() => {
    updateTopValue()
  }, [isMenuOverlayOpen, mobileTopHeader, updateTopValue])

  // Restoring from the iOS back/forward cache keeps the menu open and the body locked,
  // which leaves the page frozen
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return
      }

      setMobileHeaderConfig((previousMobileHeaderConfig) => ({
        ...previousMobileHeaderConfig,
        mobileMenuContent: 'null',
        isMobileMenuOpen: false,
      }))
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [setMobileHeaderConfig])

  return (
    <div className={styles.mobileHeaderContainer} data-type='mobile-header'>
      <div
        className={classNames(styles.mobileHeaderSticky, {
          [styles.mobileHeaderStickyBorder]: HEADERS_WITH_BORDER.includes(mobileTopHeader),
        })}
        ref={stickyRef}
      >
        <TopHeader />
      </div>
      <div
        className={classNames(styles.mobileHeaderMenuContent, {
          [styles.mobileHeaderMenuContentOpen]: isMenuOverlayOpen,
        })}
        ref={menuRef}
      >
        <MenuContent />
      </div>
    </div>
  )
}

export default MobileHeader
